import { apiClient } from './client'
import type {
  Schedule,
  ScheduleLayer,
  ScheduleOverride,
  ScheduleHoliday,
  ScheduleUnavailability,
  OnCallResponse,
  TimelineSegment,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  CreateLayerRequest,
  UpdateLayerRequest,
  LayerTimelinesResponse,
  CreateOverrideRequest,
  CreateUnavailabilityRequest,
} from './types'


export async function listSchedules(): Promise<{ data: Schedule[]; total: number }> {
  return apiClient.get<{ data: Schedule[]; total: number }>('/api/v1/schedules')
}

export async function getSchedule(id: string): Promise<Schedule> {
  return apiClient.get<Schedule>(`/api/v1/schedules/${id}`)
}

export async function createSchedule(body: CreateScheduleRequest): Promise<Schedule> {
  return apiClient.post<Schedule>('/api/v1/schedules', body)
}

export async function updateSchedule(id: string, body: UpdateScheduleRequest): Promise<Schedule> {
  return apiClient.patch<Schedule>(`/api/v1/schedules/${id}`, body)
}

export async function deleteSchedule(id: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/schedules/${id}`)
}

export async function createLayer(scheduleId: string, body: CreateLayerRequest): Promise<void> {
  return apiClient.post<void>(`/api/v1/schedules/${scheduleId}/layers`, body)
}

export async function deleteLayer(scheduleId: string, layerId: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/schedules/${scheduleId}/layers/${layerId}`)
}

export async function updateLayer(
  scheduleId: string,
  layerId: string,
  req: UpdateLayerRequest,
): Promise<ScheduleLayer> {
  return apiClient.patch<ScheduleLayer>(`/api/v1/schedules/${scheduleId}/layers/${layerId}`, req)
}

export async function getLayerTimelines(
  scheduleId: string,
  from: string,
  to: string,
): Promise<LayerTimelinesResponse> {
  return apiClient.get<LayerTimelinesResponse>(
    `/api/v1/schedules/${scheduleId}/layer-timelines?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  )
}

export async function getOnCall(scheduleId: string): Promise<OnCallResponse> {
  return apiClient.get<OnCallResponse>(`/api/v1/schedules/${scheduleId}/oncall`)
}

export async function getTimeline(
  scheduleId: string,
  from: string,
  to: string,
): Promise<{ schedule_id: string; from: string; to: string; segments: TimelineSegment[] }> {
  return apiClient.get(`/api/v1/schedules/${scheduleId}/oncall/timeline`, { from, to })
}

export async function listOverrides(
  scheduleId: string,
): Promise<{ data: ScheduleOverride[]; total: number }> {
  return apiClient.get<{ data: ScheduleOverride[]; total: number }>(
    `/api/v1/schedules/${scheduleId}/overrides`,
  )
}

export async function createOverride(
  scheduleId: string,
  body: CreateOverrideRequest,
): Promise<ScheduleOverride> {
  return apiClient.post<ScheduleOverride>(`/api/v1/schedules/${scheduleId}/overrides`, body)
}

export async function deleteOverride(scheduleId: string, overrideId: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/schedules/${scheduleId}/overrides/${overrideId}`)
}

export async function listUnavailabilities(
  scheduleId: string,
): Promise<ScheduleUnavailability[]> {
  return apiClient.get<ScheduleUnavailability[]>(`/api/v1/schedules/${scheduleId}/unavailabilities`)
}

export async function createUnavailability(
  scheduleId: string,
  body: CreateUnavailabilityRequest,
): Promise<ScheduleUnavailability> {
  return apiClient.post<ScheduleUnavailability>(`/api/v1/schedules/${scheduleId}/unavailabilities`, body)
}

export async function deleteUnavailability(scheduleId: string, uid: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/schedules/${scheduleId}/unavailabilities/${uid}`)
}

/** includeHidden is honoured by the server for admins only. */
export async function getHolidays(
  scheduleId: string,
  from: string,
  to: string,
  includeHidden = false,
): Promise<{ data: ScheduleHoliday[]; total: number }> {
  const hidden = includeHidden ? '&include_hidden=true' : ''
  return apiClient.get(`/api/v1/schedules/${scheduleId}/holidays?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${hidden}`)
}

export async function createHoliday(scheduleId: string, body: { date: string; name: string }): Promise<ScheduleHoliday> {
  return apiClient.post<ScheduleHoliday>(`/api/v1/schedules/${scheduleId}/holidays`, body)
}

export async function updateHoliday(
  scheduleId: string,
  holidayId: string,
  body: { name?: string; hidden?: boolean },
): Promise<ScheduleHoliday> {
  return apiClient.patch<ScheduleHoliday>(`/api/v1/schedules/${scheduleId}/holidays/${holidayId}`, body)
}

/** Removes an admin-added holiday, or hides a holiday that came from the country feed. */
export async function deleteHoliday(scheduleId: string, holidayId: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/schedules/${scheduleId}/holidays/${holidayId}`)
}

export const SUPPORTED_HOLIDAY_COUNTRIES: { code: string; label: string }[] = [
  { code: 'AU', label: 'Australia' },
  { code: 'CA', label: 'Canada' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'IN', label: 'India' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
]
